import React, { useEffect, useRef, useState } from 'react';
import { Seo } from './components/Seo';
import { MetaPixel } from './components/MetaPixel';
import { BundleMockup, BonusCover } from './components/BundleMockup';
import { scrollToOffer, goToCheckout } from './lib/checkout';
import { PRICE, ORIGINAL_PRICE, BONUS_IMAGES, SUPPORT_EMAIL, INSTAGRAM_HANDLE, INSTAGRAM_URL } from './data/config';
import { VOLUMES, PAINS, INSIDE, PREVIEW, BONUSES, STEPS, FAQ, Volume } from './data/content';
const BONUS_TOTAL = BONUSES.reduce((s, b) => s + b.value, 0);
const TOTAL_VALUE = ORIGINAL_PRICE + BONUS_TOTAL;
const TEMPLATE_COUNT = VOLUMES.reduce((s, v) => s + v.templates.length, 0);
function Label({
  children,
  light = false
}: {
  children: React.ReactNode;
  light?: boolean;
}) {
  return <p className={`text-[11px] font-bold uppercase tracking-[0.22em] ${light ? 'text-terra' : 'text-sage'}`} data-id="element-0" data-mp="2whxcnx635uha">{children}</p>;
}

// Scroll-only button (never goes to checkout).
function SeeButton({
  text
}: {
  text: string;
}) {
  return <button type="button" onClick={scrollToOffer} className="w-full rounded-full bg-terra px-5 py-4 text-lg font-extrabold uppercase tracking-wide text-white shadow-[0_6px_0_#b77a1f] transition hover:brightness-105 active:translate-y-[3px] active:shadow-[0_3px_0_#b77a1f] sm:text-xl" data-id="element-1" data-mp="3ikha0f5th31m">
      {text} ↓
    </button>;
}
function VolumeItem({
  v,
  open,
  onToggle
}: {
  v: Volume;
  open: boolean;
  onToggle: () => void;
}) {
  return <div className="card overflow-hidden" data-id="element-2" data-mp="2zmwh8wzxlkbq">
      <button type="button" onClick={onToggle} className="flex w-full items-center gap-3 p-4 text-left" aria-expanded={open} data-id="element-3" data-mp="33m5am0r27g6r">
        <span className="font-display flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-sage text-xl font-extrabold text-cream" data-id="element-4" data-mp="38l04bxqkep5n">{v.num}</span>
        <span className="min-w-0 flex-1" data-id="element-5" data-mp="nvj1lae5u4n3">
          <span className="block text-[10px] font-bold tracking-[0.15em] text-muted" data-id="element-6" data-mp="nur6e0oh68xc">
            CATEGORY {v.num} · {v.templates.length} TEMPLATES · {v.range.toUpperCase()}
          </span>
          <span className="font-display block text-lg font-bold leading-tight sm:text-xl" data-id="element-7" data-mp="35gm54lhq9kk">{v.title}</span>
          <span className="block text-xs italic text-ink/70" data-id="element-8" data-mp="3ubzsa6gdtbcj">“{v.pack}”</span>
        </span>
        <span className={`shrink-0 text-2xl font-bold transition ${open ? 'rotate-45 text-terra' : 'text-sage'}`} data-id="element-9" data-mp="d3mmvivk99nu">+</span>
      </button>
      {open && <div className="border-t border-line bg-creamy/60 p-4" data-id="element-10" data-mp="3gle7gc7darc0">
          <p className="text-sm text-ink/85" data-id="element-11" data-mp="3glwabrojc3sw">{v.summary}</p>
          <ol className="mt-3 grid gap-x-4 gap-y-1.5 sm:grid-cols-2" data-id="element-12" data-mp="3bmtft166lfz6">
            {v.templates.map(([id, title]) => <li key={id} className="flex min-w-0 gap-2 text-sm" data-id="element-13" data-mp="3qbzfsrcufa84">
                <span className="shrink-0 text-[12px] font-bold text-terra" data-id="element-14" data-mp="3lkr2hk8e74po">{id}</span>
                <span className="min-w-0 break-words" data-id="element-15" data-mp="2xuwt5cna7ya6">{title}</span>
              </li>)}
          </ol>
          <p className="mt-3 text-xs font-semibold text-sage" data-id="element-16" data-mp="2swbs5b07hako">Every template in editable Word + PDF</p>
        </div>}
    </div>;
}
function FaqItem({
  q,
  a
}: {
  q: string;
  a: string;
}) {
  const [open, setOpen] = useState(false);
  return <div className="border-b border-line" data-id="element-17" data-mp="1d5sq72bz8dwg">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-start justify-between gap-3 py-4 text-left" aria-expanded={open} data-id="element-18" data-mp="yfva79o7auic">
        <span className="font-semibold" data-id="element-19" data-mp="13ey541agvkcl">{q}</span>
        <span className={`shrink-0 text-xl font-bold transition ${open ? 'rotate-45 text-terra' : 'text-sage'}`} data-id="element-20" data-mp="1r3r5c9y4ged0">+</span>
      </button>
      {open && <p className="pb-4 text-sm leading-relaxed text-ink/85" data-id="element-21" data-mp="1w2u0n2oav67s">{a}</p>}
    </div>;
}

// Mobile-only sticky bar: scrolls to #offer, hidden near the top and while the offer is visible.
function StickyBar() {
  const [show, setShow] = useState(false);
  const offerVisible = useRef(false);
  const scrolled = useRef(false);
  useEffect(() => {
    const update = () => setShow(scrolled.current && !offerVisible.current);
    const onScroll = () => {
      scrolled.current = window.scrollY > 520;
      update();
    };
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    const el = document.getElementById('offer');
    let io: IntersectionObserver | null = null;
    if (el && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(entries => {
        offerVisible.current = entries[0].isIntersecting;
        update();
      }, {
        threshold: 0.05
      });
      io.observe(el);
    }
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      io?.disconnect();
    };
  }, []);
  return <div className={`fixed inset-x-0 bottom-0 z-40 border-t border-line bg-white/95 px-3 py-2 backdrop-blur transition-transform duration-300 md:hidden ${show ? 'translate-y-0' : 'translate-y-full'}`} data-id="element-22" data-mp="zwiv96lhc52l">
      <div className="flex items-center gap-3" data-id="element-23" data-mp="3kpm9cizjvjyi">
        <div className="min-w-0 flex-1 leading-tight" data-id="element-24" data-mp="3gmplacr8qvn6">
          <p className="truncate text-sm font-extrabold uppercase" data-id="element-25" data-mp="3gl63uitumejt">{TEMPLATE_COUNT} Templates · Word + PDF</p>
          <p className="text-xs" data-id="element-26" data-mp="1w2c2r9ipe043">
            <span className="text-muted line-through" data-id="element-27" data-mp="205qtov86k0wb">${ORIGINAL_PRICE}</span> <span className="font-bold text-terra" data-id="element-28" data-mp="203xolkd7toqf">${PRICE}</span>
          </p>
        </div>
        <button type="button" onClick={scrollToOffer} className="shrink-0 rounded-full bg-terra px-4 py-2 text-sm font-bold uppercase text-white" data-id="element-29" data-mp="253sebkl9889w">
          See offer ↓
        </button>
      </div>
    </div>;
}
export function App() {
  const [openVol, setOpenVol] = useState<number>(0);
  return <div className="min-h-screen w-full overflow-x-hidden bg-[#fbfcfd]" data-id="element-30" data-mp="2jw40oi95uqav">
      <Seo data-id="element-31" data-mp="2nww8r86aqp5k" />
      <MetaPixel data-id="element-32" data-mp="2nw4hg9jqfdl3" />

      <header className="border-b border-line bg-white" data-id="element-33" data-mp="2ntrypu65gz6z">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3" data-id="element-34" data-mp="2stmm3nqpv6na">
          <p className="font-display text-lg font-extrabold tracking-wide text-sage" data-id="element-35" data-mp="timy6yqd12vv">
            SIGMA SMART<span className="text-terra" data-id="element-36" data-mp="yhprxn0w3moj">™</span>
          </p>
          <p className="truncate text-sm font-extrabold uppercase tracking-wide text-terra" data-id="element-37" data-mp="21rki5uw2agca">Therapists</p>
        </div>
      </header>

      {/* 1. HERO */}
      <section className="bg-sagegrad text-white" data-id="element-38" data-mp="1wu263vmqzl02">
        <div className="mx-auto grid max-w-5xl items-center gap-8 px-4 py-10 md:grid-cols-2 md:py-16" data-id="element-39" data-mp="2as0k2zx5pff1">
          <div className="min-w-0" data-id="element-40" data-mp="25snwgcygo4be">
            <span className="pill bg-white/15 text-cream" data-id="element-41" data-mp="25rm7d4km1hao">FOR THERAPISTS, COUNSELORS, LCSWS, MFTS & PSYCHOLOGISTS</span>
            <h1 className="font-display mt-4 text-[40px] font-extrabold leading-[1.02] sm:text-6xl" data-id="element-42" data-mp="2kobv7ml0fz7w">
              Stop building paperwork from scratch.
              <br data-id="element-43" data-mp="2tj00vduhzix8" />
              <span className="text-terra" data-id="element-44" data-mp="2thglrqkkvdx1">Get back to your clients.</span>
            </h1>
            <p className="mt-4 text-base leading-relaxed text-white/90 sm:text-lg" data-id="element-45" data-mp="2pi7tkq56ro3l">
              <strong className="text-white" data-id="element-46" data-mp="33g4qxorgryjb">{TEMPLATE_COUNT} done-for-you templates</strong> for your private practice, in <strong className="text-white" data-id="element-47" data-mp="2yiw3p9rgl4b7">editable Word + PDF</strong>: intake,
              consent, SOAP, DAP and BIRP notes, treatment plans, billing and client letters.
            </p>
            <ul className="mt-4 space-y-1.5 text-sm text-white/90" data-id="element-48" data-mp="3n2zk1cbjq8af">
              <li data-id="element-49" data-mp="385gw2c6ozz41">✔ 6 categories · {TEMPLATE_COUNT} templates</li>
              <li data-id="element-50" data-mp="38576gppk7tyv">✔ In-person and telehealth ready</li>
              <li data-id="element-51" data-mp="3d3qseq45l5r8">✔ + 4 bonuses included free</li>
            </ul>
          </div>
          <div className="min-w-0" data-id="element-52" data-mp="2zdeziozqv2z1">
            <BundleMockup data-id="element-53" data-mp="2zcdafglw8fyb" />
            <div className="mt-4" data-id="element-54" data-mp="2zbu0am2hivuu">
              <SeeButton text={`See the ${TEMPLATE_COUNT} templates now`} data-id="element-55" data-mp="343lmkkelyff6" />
            </div>
            <p className="mt-3 text-center text-[12px] text-white/75" data-id="element-56" data-mp="2pf7k4al3ssz5">Instant download · 7-day risk-free guarantee</p>
          </div>
        </div>
      </section>

      {/* 2. SOUND FAMILIAR */}
      <section className="bg-creamy" data-id="element-57" data-mp="2ge9clibgi3bt">
        <div className="mx-auto max-w-5xl px-4 py-14" data-id="element-58" data-mp="2gfc5fvscafud">
          <Label data-id="element-59" data-mp="2kfuxnqy59nx3">Sound familiar?</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-60" data-mp="2ket8yjo7h2ww">You became a therapist for your clients. Not for the paperwork.</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-61" data-mp="2vwanldhiu188">
            {PAINS.map(p => <div key={p.title} className="card min-w-0 p-5" data-id="element-62" data-mp="2qwxtc7oblrvm">
                <span className="pill bg-sage/10 text-sage" data-id="element-63" data-mp="2qvd0cmhnmgzv">{p.tag}</span>
                <h3 className="font-display mt-3 text-xl font-bold leading-tight" data-id="element-64" data-mp="2lxbg3y8hsbmt">{p.title}</h3>
                <p className="mt-1 text-sm text-ink/80" data-id="element-65" data-mp="2hxb1odle80fr">{p.text}</p>
              </div>)}
          </div>
          <p className="mx-auto mt-8 max-w-2xl text-center text-lg" data-id="element-66" data-mp="2d481vnq0vur4">
            You already know what good documentation looks like. This gives you the forms, written and ready, so your evenings go back to your life.
          </p>
        </div>
      </section>

      {/* 3. CATEGORIES */}
      <section className="bg-white" data-id="element-67" data-mp="1y78sbubm1ru0">
        <div className="mx-auto max-w-3xl px-4 py-14" data-id="element-68" data-mp="1yad0t3w3z9qh">
          <Label data-id="element-69" data-mp="1u888c8hecj5f">6 categories · {TEMPLATE_COUNT} templates · Word + PDF</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-70" data-mp="9pnyeq318hwz">Every form your practice needs, from first call to discharge.</h2>
          <p className="mt-3 text-ink/80" data-id="element-71" data-mp="dsccbc6lsf0t">Open each category to see every template included.</p>
          <div className="mt-8 space-y-4" data-id="element-72" data-mp="inrmnu5ejssh">
            {VOLUMES.map((v, i) => <VolumeItem key={v.num} v={v} open={openVol === i} onToggle={() => setOpenVol(openVol === i ? -1 : i)} data-id="element-73" data-mp="nnw1zgukqhh4" data-mpi={i} />)}
          </div>
        </div>
      </section>

      {/* 4. INSIDE EVERY TEMPLATE + PREVIEW */}
      <section className="bg-sagegrad text-white" data-id="element-74" data-mp="3w4h54h0iygcb">
        <div className="mx-auto max-w-5xl px-4 py-14" data-id="element-75" data-mp="3w3pb3amksqo5">
          <Label light data-id="element-76" data-mp="4pjip29y7r7t">Inside every template</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-77" data-mp="4re38fhb5hbd">Professional, complete and ready to use.</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-78" data-mp="38ewkltoq3b0x">
            {INSIDE.map((p, i) => <div key={p.title} className="min-w-0 rounded-2xl border border-white/20 bg-white/10 p-5" data-id="element-79" data-mp="2f7n17jiztvfq" data-mpi={i}>
                <p className="text-[11px] font-bold text-terra" data-id="element-80" data-mp="2k5ot8tyed53c">{String(i + 1).padStart(2, '0')}</p>
                <h3 className="font-display mt-1 text-xl font-bold" data-id="element-81" data-mp="2p4rmzi8xfow0">{p.title}</h3>
                <p className="mt-1 text-sm text-white/85" data-id="element-82" data-mp="2u44c69n9t81r">{p.text}</p>
              </div>)}
          </div>

          <div className="mt-10 min-w-0 rounded-2xl bg-white p-5 text-ink sm:p-7" data-id="element-83" data-mp="1whf2ddmbtr5c">
            <span className="pill bg-terra/15 text-terra" data-id="element-84" data-mp="21fgv6q9k14u0">REAL TEMPLATE PREVIEW</span>
            <h3 className="font-display mt-3 text-2xl font-extrabold leading-tight text-sage" data-id="element-85" data-mp="26etitd892fxn">{PREVIEW.title}</h3>
            <p className="mt-3 text-sm" data-id="element-86" data-mp="2b59zd67daho8"><strong data-id="element-87" data-mp="2b6l9rovbinob">What it is:</strong> {PREVIEW.goal}</p>
            <p className="mt-1 text-sm" data-id="element-88" data-mp="1dq5rp10tkwc6"><strong data-id="element-89" data-mp="1dqxnocybww2z">Best for:</strong> {PREVIEW.bestFor}</p>
            <p className="mt-1 text-sm text-muted" data-id="element-90" data-mp="1dobgiefcoila">{PREVIEW.time}</p>
            <p className="mt-4 text-sm font-bold" data-id="element-91" data-mp="1ino5b4ps7zqi">Sections</p>
            <ol className="mt-2 space-y-1.5" data-id="element-92" data-mp="3w00jpbh5z22n">
              {PREVIEW.steps.map((s, i) => <li key={s} className="flex min-w-0 gap-2 text-sm" data-id="element-93" data-mp="3rxdrh9gkzf4x" data-mpi={i}>
                  <span className="shrink-0 font-bold text-terra" data-id="element-94" data-mp="3rw2nb8j845da">{i + 1}.</span>
                  <span className="min-w-0 break-words" data-id="element-95" data-mp="3mwzi9o38tz5b">{s}</span>
                </li>)}
            </ol>
            <p className="mt-4 text-sm font-bold" data-id="element-96" data-mp="3i23mccuvyrmx">Also included</p>
            <ul className="mt-2 space-y-1" data-id="element-97" data-mp="3d27jwi1u789h">
              {PREVIEW.questions.map(q => <li key={q} className="text-sm text-ink/85" data-id="element-98" data-mp="3894oe4di1oqn">• {q}</li>)}
            </ul>
            <p className="mt-4 rounded-xl bg-creamy p-3 text-xs text-ink/80" data-id="element-99" data-mp="3494aqly85hkn">Templates only. Adapt to your state laws and licensing board requirements.</p>
          </div>
        </div>
      </section>

      {/* 5. BONUSES */}
      <section className="bg-creamy" data-id="element-100" data-mp="2uc9bg0u4nd5b">
        <div className="mx-auto max-w-5xl px-4 py-14" data-id="element-101" data-mp="2pajzmiz2yvg6">
          <Label data-id="element-102" data-mp="2pbtxb720u1ma">Included free · 4 bonuses</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-103" data-mp="2fn6lk3g54bxp">Plus 4 bonuses for your practice.</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2" data-id="element-104" data-mp="2ko42kcpeyzxm">
            {BONUSES.map((b, i) => <div key={b.title} className="card flex min-w-0 gap-4 p-4" data-id="element-105" data-mp="26ocgdooqud8j" data-mpi={i}>
                <div className="w-[38%] max-w-[150px] shrink-0" data-id="element-106" data-mp="2anld92f2yr8b">
                  <BonusCover index={i} image={BONUS_IMAGES[i] || ''} short={b.short} data-id="element-107" data-mp="2ydxoam5mh434" />
                </div>
                <div className="min-w-0 flex-1" data-id="element-108" data-mp="33cr4wng7hb7i">
                  <p className="text-[11px] font-bold text-terra" data-id="element-109" data-mp="33citz13n8fyy">BONUS {i + 1}</p>
                  <h3 className="font-display text-lg font-bold leading-tight sm:text-xl" data-id="element-110" data-mp="2pkugouhnrlge">{b.title}</h3>
                  <p className="mt-1 text-sm text-ink/80" data-id="element-111" data-mp="2tlwgbcfhlziw">{b.text}</p>
                  <p className="mt-2 text-sm" data-id="element-112" data-mp="3hca0kgrtyu2x">
                    <span className="text-muted line-through" data-id="element-113" data-mp="3hbihg5ff6otj">${b.value}</span> <span className="font-bold text-go" data-id="element-114" data-mp="3m3rzz0e7gmls">FREE</span>
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6. HOW IT WORKS */}
      <section className="bg-white" data-id="element-115" data-mp="15oooeg43bz6w">
        <div className="mx-auto max-w-5xl px-4 py-14" data-id="element-116" data-mp="15nlz2cmf24t3">
          <Label data-id="element-117" data-mp="1kdb62oh6x31a">How it works</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-118" data-mp="1kgfclsi4oav4">Download. Personalize. Use it today.</h2>
          <div className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-119" data-mp="rp5gnof6ypv4">
            {STEPS.map(s => <div key={s.n} className="card min-w-0 p-5" data-id="element-120" data-mp="mqu8rj8gipfj">
                <span className="font-display text-5xl font-extrabold leading-none text-terra" data-id="element-121" data-mp="msf1r4f4i0ba">{s.n}</span>
                <h3 className="font-display mt-3 text-xl font-bold" data-id="element-122" data-mp="11ls2ogj97myq">{s.title}</h3>
                <p className="mt-1 text-sm text-ink/80" data-id="element-123" data-mp="wn738jbtt7bc">{s.text}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 7. OFFER — the ONLY checkout button lives here */}
      <section id="offer" className="bg-sagegrad scroll-mt-4 text-white" data-id="element-124" data-mp="283nlekerm21w">
        <div className="mx-auto max-w-xl px-4 py-14" data-id="element-125" data-mp="ho4tu9oxpyi2">
          <div className="text-center" data-id="element-126" data-mp="mlwmx71bfqnu">
            <Label light data-id="element-127" data-mp="mmxzjfxzcl7o">The offer</Label>
            <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-128" data-mp="mnr6oi0upcqc">Everything you get</h2>
          </div>
          <div className="mt-6 overflow-hidden rounded-2xl bg-white text-ink shadow-2xl" data-id="element-129" data-mp="vn48coiqgx0i">
            <div className="p-3" data-id="element-130" data-mp="10lxo6nlht03u">
              <BundleMockup compact data-id="element-131" data-mp="10kwasch081iy" />
            </div>
            <div className="p-4 pt-0 sm:p-6 sm:pt-0" data-id="element-132" data-mp="15kin8jp0ddqp">
              <ul className="divide-y divide-line text-sm" data-id="element-133" data-mp="15j7jukzh6804">
                <li className="flex items-center justify-between gap-3 py-3" data-id="element-134" data-mp="1aaz3ebk7rdgr">
                  <span className="min-w-0 font-semibold" data-id="element-135" data-mp="1ed2m9l97pkc2">📘 120+ Done-For-You Templates for Therapists™ · {TEMPLATE_COUNT} templates, Word + PDF</span>
                  <span className="shrink-0 text-muted line-through" data-id="element-136" data-mp="1jdqe2959m57c">${ORIGINAL_PRICE}</span>
                </li>
                {BONUSES.map(b => <li key={b.title} className="flex items-center justify-between gap-3 py-3" data-id="element-137" data-mp="1yex9zpujuhwk">
                    <span className="flex min-w-0 items-start gap-2" data-id="element-138" data-mp="1tgdkxgknspzz">
                      <span className="shrink-0 text-base leading-none" data-id="element-139" data-mp="1ti6sqz70dg9k">🎁</span>
                      <span className="min-w-0" data-id="element-140" data-mp="1ohj2wguin5gx">{b.title}</span>
                    </span>
                    <span className="shrink-0 whitespace-nowrap text-right" data-id="element-141" data-mp="1kgqshk9wquj2">
                      <span className="text-muted line-through" data-id="element-142" data-mp="2h5y6m78761cy">${b.value}</span> <span className="font-bold text-go" data-id="element-143" data-mp="2h7iyfp34n674">FREE</span>
                    </span>
                  </li>)}
                <li className="flex items-center gap-2 py-3" data-id="element-144" data-mp="27f3i0w7hapoc">
                  <span className="shrink-0 font-bold text-go" data-id="element-145" data-mp="27dion9wwhcs2">✔</span>
                  <span className="font-semibold" data-id="element-146" data-mp="23drz19j412p4">7-Day Risk-Free Guarantee</span>
                </li>
                <li className="flex items-center gap-2 py-3" data-id="element-147" data-mp="2zxr1662wzums">
                  <span className="shrink-0 font-bold text-go" data-id="element-148" data-mp="2v57k2z50qhd8">✔</span>
                  <span className="font-semibold" data-id="element-149" data-mp="t7xcmn8sdzp5">Lifetime Access &amp; Free Updates</span>
                </li>
              </ul>
              <div className="mt-4 border-t border-line pt-4 text-center" data-id="element-150" data-mp="y7a1fdj7xgud">
                <p className="text-sm" data-id="element-151" data-mp="jidtdgh5w3tr">
                  Total value: <span className="text-muted line-through" data-id="element-152" data-mp="jhku10kj8gln">${TOTAL_VALUE}</span>
                </p>
                <p className="mt-1 text-sm" data-id="element-153" data-mp="ofmb5fyee5uh">
                  Regular price <span className="line-through" data-id="element-154" data-mp="agweg1fhq86n">${ORIGINAL_PRICE}</span> · your price, one-time payment:
                </p>
                <p className="font-display text-7xl font-extrabold leading-none text-sage" data-id="element-155" data-mp="fhad4sv7c5s0">${PRICE}</p>
                <p className="mt-3 text-xs text-muted" data-id="element-156" data-mp="kjlx6vom0dh">· Payment will be processed in your local currency ·</p>
                <button type="button" onClick={goToCheckout} className="mt-3 w-full rounded-full bg-go px-4 py-4 text-lg font-extrabold uppercase tracking-wide text-white shadow-[0_5px_0_#248a16] transition hover:brightness-105 active:translate-y-[3px] active:shadow-[0_2px_0_#248a16] sm:text-xl" data-id="element-157" data-mp="5il7bygkybs4">
                  Get the {TEMPLATE_COUNT} templates now
                </button>
                <p className="mt-3 text-[11px] text-muted" data-id="element-158" data-mp="3u4pzd8i4cvw1">Secure checkout · Instant download</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. GUARANTEE */}
      <section className="bg-creamy" data-id="element-159" data-mp="6yyxsml5nau0">
        <div className="mx-auto max-w-3xl px-4 py-14" data-id="element-160" data-mp="6xxclpafd7yk">
          <div className="card flex flex-col items-center gap-5 p-6 text-center sm:flex-row sm:text-left" data-id="element-161" data-mp="2yg74vv00ate">
            <div className="font-display flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-sage text-3xl font-extrabold text-cream" data-id="element-162" data-mp="gxxz1k3lc46z">7</div>
            <div className="min-w-0" data-id="element-163" data-mp="bws4rhua88vc">
              <h2 className="font-display text-3xl font-extrabold leading-tight" data-id="element-164" data-mp="2io9ev0rpjp2b">7-Day Risk-Free Guarantee</h2>
              <p className="mt-2 text-ink/85" data-id="element-165" data-mp="2dqzu3yf9k1kt">
                Open the kit and use the templates with your next clients. If it does not fit your practice, email us within 7 days of purchase and you get your money back.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="bg-white" data-id="element-166" data-mp="21n3vyn4vmy1k">
        <div className="mx-auto max-w-3xl px-4 py-14" data-id="element-167" data-mp="26loxqqzs1ps4">
          <Label data-id="element-168" data-mp="26l5ftaa4n1e3">Questions</Label>
          <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-169" data-mp="26nj7ra9ifxhf">Frequently asked questions</h2>
          <div className="mt-6 border-t border-line" data-id="element-170" data-mp="1dwhooy2p5hsm">
            {FAQ.map(f => <FaqItem key={f.q} q={f.q} a={f.a} data-id="element-171" data-mp="1iw43hby6b63j" />)}
          </div>
        </div>
      </section>

      {/* 10. FINAL CTA (scrolls to the offer) */}
      <section className="bg-sagegrad text-white" data-id="element-172" data-mp="1nws0qfczwj67">
        <div className="mx-auto max-w-2xl px-4 py-14 text-center" data-id="element-173" data-mp="2ubsq227im33t">
          <h2 className="font-display text-3xl font-extrabold leading-tight sm:text-5xl" data-id="element-174" data-mp="2udly9lxs0vdx">
            Less time on paperwork.
            <br data-id="element-175" data-mp="nmqynymmudyk" />
            <span className="text-terra" data-id="element-176" data-mp="nna9kvdv8233">More time for your clients.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-white/85" data-id="element-177" data-mp="ine6cyczseol">{TEMPLATE_COUNT} templates in Word + PDF and 4 bonuses, ready for your next client.</p>
          <div className="mx-auto mt-6 max-w-md" data-id="element-178" data-mp="dvcxzniy383u">
            <SeeButton text={`See the ${TEMPLATE_COUNT} templates now`} data-id="element-179" data-mp="8ymdmo03xqcm" />
          </div>
        </div>
      </section>

      {/* 11. FOOTER */}
      <footer className="border-t border-line bg-white pb-24 md:pb-10" data-id="element-180" data-mp="40ck0hu16xt4">
        <div className="mx-auto max-w-3xl px-4 pt-10 text-center" data-id="element-181" data-mp="3w1r5p4qmelts">
          <p className="font-display text-2xl font-extrabold tracking-wide text-sage" data-id="element-182" data-mp="3r3hawv8t5n8p">
            SIGMA SMART<span className="text-terra" data-id="element-183" data-mp="3m4wbuz5al9lu">™</span>
          </p>
          <div className="mx-auto mt-6 max-w-md" data-id="element-184" data-mp="3m6h8qvf2x4mv">
            <BundleMockup compact data-id="element-185" data-mp="1pcmdy02v7i0e" />
          </div>
          <p className="mt-6 text-sm" data-id="element-186" data-mp="1pa9xxsgo3hpz">
            📧{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="break-all font-semibold underline" data-id="element-187" data-mp="1keb3po8nr5hn">
              {SUPPORT_EMAIL}
            </a>
          </p>
          <p className="mt-2 text-sm" data-id="element-188" data-mp="2c96x7ddsda7o">
            📲{' '}
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="font-semibold underline" data-id="element-189" data-mp="279kjl2u1pryc">
              {INSTAGRAM_HANDLE}
            </a>
          </p>
          <p className="mx-auto mt-6 max-w-2xl text-xs leading-relaxed text-muted" data-id="element-190" data-mp="1xj7xv7ahvsqm">
            Disclaimer: this is a digital kit of document templates for licensed mental health professionals. Templates only: they are not legal, billing or clinical advice
            and do not replace your professional judgment. Requirements vary by state, payer and licensing board; review and adapt every template to your state laws and
            licensing board requirements before use.
          </p>
          <p className="mt-4 text-[11px] text-muted" data-id="element-191" data-mp="3dwy52mwwux4h">© {new Date().getFullYear()} SIGMA SMART™ · All rights reserved.</p>
        </div>
      </footer>

      {/* 12. MOBILE STICKY BAR (scrolls to the offer only) */}
      <StickyBar data-id="element-192" data-mp="3hdkjw7hd2wsn" />
    </div>;
}